import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCanvas } from "../../../../context/CanvasContext";
import ConfirmationModal from "../../../../../shared/components/modals/ConfirmationModal";
import EditModal from "../../../../../shared/components/modals/EditModal";
import InstructionsModal from "../../../../../shared/components/InstructionsModal";
import GenericDetailsModal from "../../../../../shared/components/modals/GenericDetailsModal";
import GenericCard from "../../../../../shared/components/GenericCard";
import { FiTrendingUp } from "react-icons/fi";
import { toast } from "react-toastify";
import {
  fetchSavings,
  createSaving,
  updateSaving,
  deleteSaving,
  uploadSavings,
  selectSavings,
  selectSavingLoading,
  selectSavingPagination,
  savingOverview,
  selectSavingOverview, 
} from "../../../../../../app/store/slices/savingSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import Pagination from '../../../../../shared/components/Pagination';
import FileUploadButton from "../../../../../shared/components/FileUploadButton";
import FormInput from "../../../../../shared/ui/FormInput";
import FormSelect from "../../../../../shared/ui/FormSelect";
import FormTextarea from "../../../../../shared/ui/FormTextarea";
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import Spinner from '../../../../../shared/components/Spinner';
import SummaryCard from "../../../../../shared/components/SummaryCard";
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import { GiCoins, GiCash } from "react-icons/gi";
import {formatAmount} from '../../../../../../utils/formatAmount';




const SavingContent = () => {
  const dispatch = useDispatch();
  const { selectedCanvas } = useCanvas();
  const savings = useSelector(selectSavings);
  const loading = useSelector(selectSavingLoading);
  const pagination = useSelector(selectSavingPagination);
  const accounts = useSelector(selectAccounts);
  const overview = useSelector(selectSavingOverview); 

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);


  // Data states
  const [selectedSaving, setSelectedSaving] = useState(null);
  const [editSavingData, setEditSavingData] = useState({
    title: "",
    targetAmount: "",
    currentAmount: "",
    targetDate: "",
    paymentAccountId: "",
    destinationAccountId  :"",
    notes: "",
    status: "active",
  });
  const [addSavingData, setAddSavingData] = useState({
    title: "",
    targetAmount: "",
    currentAmount: "",
    targetDate: "",
    paymentAccountId: "",
    destinationAccountId: "", 
    notes: "",
    status: "active",
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef();
  const addOptionsRef = useRef(null);
  const baseCurrency = selectedCanvas?.currency || "";
  
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  // Format currency
  const formatCurrency = (amount) => {
    return `${baseCurrency} ${formatAmount(amount)}`;
  };

  // File upload template download
  const handleDownloadTemplate = () => {
  try {
    const sampleAccountName = accounts.length > 0 ? accounts[0].name : 'account_name_here';

    const templateData = [
      { 
        title: 'Car Savings', 
        targetAmount: '25000', 
        currentAmount: '5000', 
        targetDate: '2025-12-31', 
        paymentAccountId: sampleAccountName,
        destinationAccountId: sampleAccountName,
        notes: 'Saving for a new car'
      },
      { 
        title: 'Emergency Fund', 
        targetAmount: '10000', 
        currentAmount: '2500', 
        targetDate: '2025-06-30', 
        paymentAccountId: sampleAccountName,
        destinationAccountId: sampleAccountName,
        notes: '6 months of expenses'
      },
      { 
        title: 'Vacation Fund', 
        targetAmount: '5000', 
        currentAmount: '1200', 
        targetDate: '2025-08-15', 
        paymentAccountId: sampleAccountName,
        destinationAccountId: sampleAccountName,
        notes: 'Summer vacation to Europe'
      }
    ];

    const csvContent = [
      'title,targetAmount,currentAmount,targetDate,paymentAccountId,destinationAccountId,notes',
      ...templateData.map(row => 
        `${row.title},${row.targetAmount},${row.currentAmount},${row.targetDate},${row.paymentAccountId},${row.destinationAccountId},"${row.notes}"`
      )
    ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `savings_template_${baseCurrency}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setShowInstructions(false)
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };
  const handleAddSavingAccountChange = (e) => {
  const paymentAccountId = e.target.value;
  setAddSavingData(prev => ({
    ...prev,
    paymentAccountId,
    destinationAccountId: paymentAccountId
  }));
};

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchSavings({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchAccounts({ canvasId: selectedCanvas.id, limit:1000 }));
      dispatch(savingOverview({ canvasId: selectedCanvas.id })); 
    }
  }, [dispatch, selectedCanvas?.id, currentPage, pageSize]);

  // Autofill edit form
  useEffect(() => {
    if (showEditForm && selectedSaving) {
      setEditSavingData({
        title: selectedSaving.title || "",
        targetAmount: selectedSaving.targetAmount ?? "",
        currentAmount: selectedSaving.currentAmount ?? "",
        targetDate: selectedSaving.targetDate ? selectedSaving.targetDate.slice(0, 10) : "",
        paymentAccountId: selectedSaving.paymentAccountId || "", 
        destinationAccountId:selectedSaving.destinationAccountId || '',
        notes: selectedSaving.notes || "",
        status: selectedSaving.status || "active", 
      });
    }
  }, [showEditForm, selectedSaving]);

  // Handle Delete
  const handleDeleteSaving = async () => {
    if (!selectedSaving || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Saving");
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(
        deleteSaving({
          canvasId: selectedCanvas.id,
          savingId: selectedSaving._id,
        })
      ).unwrap();
      setShowDeleteConfirm(false);
      setSelectedSaving(null);
      dispatch(fetchSavings({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(savingOverview({ canvasId: selectedCanvas.id })); 
      toast.success("Saving Deleted Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Edit
  const handleEditSaving = async (e) => {
    e.preventDefault();
    if (!selectedSaving || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Saving");
      return;
    }
    setIsEditing(true);
    try {
      await dispatch(
        updateSaving({
          canvasId: selectedCanvas.id,
          savingId: selectedSaving._id,
          savingData: {
            ...editSavingData,
            targetAmount: Number(editSavingData.targetAmount),
            currentAmount: Number(editSavingData.currentAmount),
            destinationAccountId: editSavingData.destinationAccountId,
          },
        })
      ).unwrap();
      setShowEditForm(false);
      setSelectedSaving(null);
      dispatch(fetchSavings({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(savingOverview({ canvasId: selectedCanvas.id })); 
      toast.success("Saving Updated Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle Add
  const handleAddSaving = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) {
      toast.error("Invalid Canvas");
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(
        createSaving({
          canvasId: selectedCanvas.id,
          savingData: {
            ...addSavingData,
            targetAmount: Number(addSavingData.targetAmount),
            currentAmount: Number(addSavingData.currentAmount || 0),
            destinationAccountId: addSavingData.destinationAccountId,
          },
        })
      ).unwrap();
      setShowAddForm(false);
      setAddSavingData({
        title: "",
        targetAmount: "",
        currentAmount: "",
        targetDate: "",
        paymentAccountId: "",
        destinationAccountId: "",
        notes: "",
        status: "active",
      });
      dispatch(fetchSavings({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(savingOverview({ canvasId: selectedCanvas.id })); 
      toast.success("Saving Created Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  const  handleUpdateSaving = async (savingId, updateData) => {
    setIsUpdating(true);
    try {
      await dispatch(
        updateSaving({
          canvasId: selectedCanvas.id,
          savingId,
          savingData: updateData,
        })
      ).unwrap();
      
      // Update the selected saving for the modal
      setSelectedSaving(prev => ({ ...prev, ...updateData }));
      
      dispatch(fetchSavings({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(savingOverview({ canvasId: selectedCanvas.id }));
      toast.success("Saving updated successfully");
    } catch (err) {
      toast.error(err?.message || 'Failed to update saving');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle Add Contribution
  const handleAddContribution = async (savingId, amount) => {
    try {
      const saving = savings.find(s => s._id === savingId);
      if (!saving) {
        toast.error("Saving not found");
        return;
      }

      const newCurrentAmount = (saving.currentAmount || 0) + amount;
      const progressPercentage = saving.targetAmount > 0 
        ? (newCurrentAmount / saving.targetAmount) * 100 
        : 0;

      const updateData = { currentAmount: newCurrentAmount };
      if (progressPercentage >= 100 && saving.status !== 'completed') {
        updateData.status = 'completed';
      }
      
      await handleUpdateSaving(savingId, updateData);
      
      const statusMessage = progressPercentage >= 100 && saving.status !== 'completed' 
        ? ` - Congratulations! Your saving goal is now completed! 🎉`
        : '';
      
    } catch (err) {
      toast.error('Failed to add contribution');
      throw err;
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCanvas?.id) {
      toast.error("Invalid Canvas ID or file");
      return;
    }
    try {
      await dispatch(uploadSavings({ canvasId: selectedCanvas.id, file })).unwrap();
      dispatch(fetchSavings({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(savingOverview({ canvasId: selectedCanvas.id })); 
      toast.success("Savings Imported Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    }
  };


  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6">Savings</h1>
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        {/* overview-Header */}
      <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6">
        <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto">
          {/*Overview-Cards*/}
          <SummaryCard
            title="Total Savings"
            value={overview?.amountSaved !== undefined ? formatCurrency(overview?.amountSaved) : "Auto calculated"}
            icon={<GiCash className="w-5 h-5" />}
            valueColor="text-green-700"
            iconBg="bg-green-100"
            iconColor="text-green-600"
            formatValue={val => (formatCurrency((overview?.amountSaved)))}
          />
          <SummaryCard
            title="Saving Rate"
            value={overview?.savingRate !== undefined ? overview?.savingRate : "Auto calculated"}
            icon={<FiTrendingUp className="w-5 h-5" />}
            valueColor="text-btn-primary"
            iconBg="bg-blue-100"
            iconColor="text-btn-primary"
            formatValue={val => typeof val === "number" ? `${formatAmount(val)}%` : val}
          />
        </div>


        <AddOptionsButton
            showOptions={showAddOptions}
            setShowOptions={setShowAddOptions}
            addOptionsRef={addOptionsRef}
            onAddManual={() => setShowAddForm(true)}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            onShowInstructions={() => setShowInstructions(true)}
            buttonLabel="Add Saving"
            buttonTitle="Add Saving"
            manualLabel="Add Manually"
            FileUploadButtonComponent={FileUploadButton}
          />
      </div>

      {/* Savings Cards Grid */}
      
        {loading ? (
          <div className="flex justify-center items-center py-12">
           <Spinner text ={'Loading Savings...'} size={'large'}/>
          </div>
        ) : savings?.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {savings.map(saving => (
                <GenericCard
                  key={saving._id}
                  item={saving}
                  baseCurrency={baseCurrency}
                  cardType="saving"
                  onEdit={() => {
                    setSelectedSaving(saving);
                    setShowEditForm(true);
                  }}
                  onDelete={() => {
                    setSelectedSaving(saving);
                    setShowDeleteConfirm(true);
                  }}
                  onViewDetails={() => {
                    setSelectedSaving(saving);
                    setShowDetailsModal(true);
                  }}
                />
              ))}
            </div>
            
            {/* Pagination */}
            <Pagination
              pagination={pagination}
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              PAGE_SIZE_OPTIONS={[9, 18, 27]}
              label="Savings per page"
              totalLabel={`Total: ${pagination?.total || 0} savings`}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
                  <GiCoins className="w-16 h-16 mx-auto" />
            </div>
            <p className="text-gray-600 text-lg mb-2">No Savings Found</p>
            <p className="text-gray-500">Start saving towards your goals by creating your first saving plan.</p>
          </div>
        )}
      </div>
      
      {/* Modals */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Saving"
        message={`Are you sure you want to delete "${selectedSaving?.title}"? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedSaving(null);
        }}
        onConfirm={handleDeleteSaving}
        loading={isDeleting}
      />

      <EditModal
        show={showEditForm}
        title="Edit Saving"
        onCancel={() => {
          setShowEditForm(false);
          setSelectedSaving(null);
        }}
        onSubmit={handleEditSaving}
        loading={isEditing}
        submitLabel="Update"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Title"
            type="text"
            value={editSavingData.title}
            onChange={e => setEditSavingData({ ...editSavingData, title: e.target.value })}
            required
          />
          <FormInput
            label="Target Amount"
            type="number"
            value={editSavingData.targetAmount}
            onChange={e => setEditSavingData({ ...editSavingData, targetAmount: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Current Amount"
            type="number"
            value={editSavingData.currentAmount}
            onChange={e => setEditSavingData({ ...editSavingData, currentAmount: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Target Date"
            type="date"
            value={editSavingData.targetDate}
            onChange={e => setEditSavingData({ ...editSavingData, targetDate: e.target.value })}
            required
          />
          <FormSelect
            label="Status"
            value={editSavingData.status}
            onChange={e => setEditSavingData({ ...editSavingData, status: e.target.value })}
            required
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </FormSelect>
          <FormSelect
            label="Account"
            value={editSavingData.paymentAccountId}
            onChange={e => setEditSavingData({ ...editSavingData, paymentAccountId: e.target.value })}
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Destination Account"
            value={editSavingData.destinationAccountId}
            onChange={e => setEditSavingData({ ...editSavingData, destinationAccountId: e.target.value })}
          >
            <option value="">Select Account</option>
            {accounts
              .filter(acc => acc.type === "Saving")
              .map(acc => (
                <option key={acc._id} value={acc._id}>{acc.name}</option>
              ))}
          </FormSelect>
          <FormTextarea
            label="Notes"
            value={editSavingData.notes}
            onChange={e => setEditSavingData({ ...editSavingData, notes: e.target.value })}
            rows={2}
          />
        </div>
      </EditModal>

      <EditModal
        show={showAddForm}
        title="Create Saving"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleAddSaving}
        loading={isAdding}
        submitLabel="Create"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Title"
            type="text"
            value={addSavingData.title}
            onChange={e => setAddSavingData({ ...addSavingData, title: e.target.value })}
            required
          />
          <FormInput
            label="Target Amount"
            type="number"
            value={addSavingData.targetAmount}
            onChange={e => setAddSavingData({ ...addSavingData, targetAmount: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Current Amount"
            type="number"
            value={addSavingData.currentAmount}
            onChange={e => setAddSavingData({ ...addSavingData, currentAmount: e.target.value })}
            min="0"
            step="0.01"
            placeholder="0"
          />
          <FormInput
            label="Target Date"
            type="date"
            value={addSavingData.targetDate}
            onChange={e => setAddSavingData({ ...addSavingData, targetDate: e.target.value })}
            required
          />
          <FormSelect
            label="Status"
            value={addSavingData.status}
            onChange={e => setAddSavingData({ ...addSavingData, status: e.target.value })}
            required
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </FormSelect>
          <FormSelect
            label="Account"
            value={addSavingData.paymentAccountId}
            onChange={handleAddSavingAccountChange}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Destination Account"
            value={addSavingData.destinationAccountId}
            onChange={e => setAddSavingData({ ...addSavingData, destinationAccountId: e.target.value })}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormTextarea
            label="Notes"
            value={addSavingData.notes}
            onChange={e => setAddSavingData({ ...addSavingData, notes: e.target.value })}
            rows={2}
            placeholder="Add notes about your saving goal (optional)"
          />
        </div>
      </EditModal>

      <GenericDetailsModal
        show={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSaving(null);
        }}
        item={selectedSaving}
        onUpdateItem={handleUpdateSaving}
        onAddTransaction={handleAddContribution}
        baseCurrency={baseCurrency}
        accounts={accounts}
        isUpdating={isUpdating}
        modalType="saving"
      />

      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="savings"
        onDownloadTemplate={handleDownloadTemplate}
      />
    </div>
  );
};

export default SavingContent;